import ChevronRight from "@/assets/colorable/right_chevron.svg?react";
import CompanyLogoPlaceholder from "@/assets/light_gradient/company_logo_placeholder.svg";
import clsxm from "@/lib/clsxm";
import StepperArrows from "@/pages/companyDashboard/StepperArrows";
import {isDraggingSlider} from "@/store/jotaiAtoms";
import useUserStore from "@/store/user";
import {useSetAtom} from "jotai";
import {debounce} from "lodash";
import {useCallback, useEffect, useMemo, useRef, useState, type FC, type WheelEvent} from "react";
import {useTranslation} from "react-i18next";
import {Link, Outlet, useNavigate, useParams} from "react-router";
import Slider, {Settings} from "react-slick";

const SETTINGS: Settings = {
  arrows: false,
  waitForAnimate: true,
  swipeToSlide: true,
  swipe: true,
  infinite: false,
  centerMode: false,
  slidesToShow: 4,
};

const CompanyEditMenu: FC = () => {
  const {t} = useTranslation(["common"]);
  const navigate = useNavigate();
  const {companyId} = useParams<{
    companyId: string;
  }>();
  const user = useUserStore(state => state.user);
  const subBrands = useMemo(() => user.sub_brands?.map(subBrand => subBrand.company) ?? [], [user.sub_brands]);
  const isViewingSelf = useMemo(
    () => user.sub_brands?.find(subBrand => subBrand.company.id === Number(companyId)),
    [user.sub_brands, companyId],
  );
  const [idx, setIdx] = useState(1);
  const ref = useRef<Slider>(null);
  const setDragging = useSetAtom(isDraggingSlider);
  const handleSideScroll = useCallback(
    debounce((e: WheelEvent<HTMLDivElement>) => {
      if (!ref.current) return;

      if (e.deltaX > 0) ref.current.slickNext();
      else if (e.deltaX < 0) ref.current.slickPrev();
    }, 20),
    [],
  );

  useEffect(() => {
    if (!!subBrands) {
      const brandIdx = subBrands.findIndex(d => d.id === Number(companyId));
      const remainingBrands = subBrands.length - brandIdx;
      const offset = Math.max(0, SETTINGS.slidesToShow! - remainingBrands);
      const idx = brandIdx - offset;

      ref.current?.slickGoTo(idx);
      setIdx(idx);
    }
  }, [subBrands, companyId]);

  return (
    <div className="max-w-8xl relative container mx-auto mt-10 mb-20 px-4 sm:mt-40">
      <button className="mb-6 flex flex-row items-center gap-7" onClick={() => navigate(-1)}>
        <ChevronRight className="h-5 w-5 rotate-180" />
        <span className="text-4xl font-semibold">{t("common:profileInformation")}</span>
      </button>
      {isViewingSelf && user.sub_brands?.length! > 1 && (
        <div
          className="relative mt-10 mb-20 border-b-1"
          onWheel={e => {
            if (subBrands.length! > 1) handleSideScroll(e);
          }}>
          <StepperArrows
            data={subBrands}
            onNext={() => {
              ref.current?.slickNext();
            }}
            onPrevious={() => {
              ref.current?.slickPrev();
            }}
            showedIndex={idx + 1}
            offset={SETTINGS.slidesToShow! - 1}
            isLoading={false}
          />
          <Slider
            ref={ref}
            className="overflow-hidden [&_.slick-track]:ml-0"
            beforeChange={() => setDragging(true)}
            afterChange={index => setIdx(index)}
            {...SETTINGS}>
            {subBrands.map((brand, idx) => (
              <Link
                key={idx}
                to={`/company/${brand?.id}/edit/`}
                className={clsxm(
                  "flex flex-row items-center justify-center gap-[10px] py-2 no-underline transition-all",
                  Number(companyId) === brand.id &&
                    typeof companyId !== "undefined" &&
                    "border-primary-dark-solid border-b-1",
                )}>
                <img
                  src={brand?.picture320 ?? CompanyLogoPlaceholder}
                  className="border-neutral-grey-lightest size-8 rounded-full border"
                />
                <span
                  className={clsxm(
                    "hover:text-primary-dark-solid line-clamp-1 font-medium",
                    Number(companyId) === brand.id && typeof companyId !== "undefined" && "text-primary-dark-solid",
                  )}>
                  {brand?.company_name}
                </span>
              </Link>
            ))}
          </Slider>
        </div>
      )}

      <Outlet />
    </div>
  );
};

export default CompanyEditMenu;
